import { Image } from 'expo-image';
import { Link } from 'expo-router';
import * as WebBrowser from 'expo-web-browser';
import type { RefObject } from 'react';
import { type GestureResponderEvent, Platform, Pressable } from 'react-native';
import type { EnrichedTextInputInstance } from 'react-native-enriched-html';
import { StyleSheet, withUnistyles } from 'react-native-unistyles';
import {
  type ChatMessageActionSheetModal,
  useChatDetailContext,
} from '@/components/Chat/ChatDetailContext';
import Box from '@/components/UI/Box';
import Text from '@/components/UI/Text';
import useChatActionItems from '@/hooks/useChatActionItems';
import { LONG_PRESS_DELAY } from '@/utils/constants';
import handleLongPress from '@/utils/handleLongPress';

const IMAGE_TRANSITION_DURATION = 500;
const UnistylesImage = withUnistyles(Image);
function presentOgActionSheet({
  bottomSheetRef,
  getActionItems,
  inputRef,
  url,
}: {
  bottomSheetRef: RefObject<ChatMessageActionSheetModal | null>;
  getActionItems: ReturnType<typeof useChatActionItems>;
  inputRef: RefObject<EnrichedTextInputInstance | null>;
  url: string;
}) {
  inputRef.current?.blur();
  bottomSheetRef.current?.present(getActionItems('', url, '', false));
}
export default function ChatMediaOgItem({
  imageUrl = null,
  url = null,
  title = null,
  description = null,
}: {
  imageUrl?: string | null;
  url?: string | null;
  title?: string | null;
  description?: string | null;
}) {
  const getActionItems = useChatActionItems();
  const bottomSheetRef = useChatDetailContext((state) => state.bottomSheetRef);
  const inputRef = useChatDetailContext((state) => state.inputRef);
  const onPress = (e: GestureResponderEvent) => {
    if (Platform.OS !== 'web' && url) {
      e.preventDefault();
      WebBrowser.openBrowserAsync(url);
    }
  };
  const onLongPress = () => {
    if (url) {
      presentOgActionSheet({
        bottomSheetRef,
        getActionItems,
        inputRef,
        url,
      });
    }
  };
  return (
    <Link
      href={{
        pathname: url || '',
      }}
      target="_blank"
      style={style.linkContainer}
    >
      <Pressable
        delayLongPress={LONG_PRESS_DELAY}
        onPress={onPress}
        onLongPress={handleLongPress(onLongPress)}
        style={({ pressed }) => [style.pressable, pressed && style.pressed]}
      >
        {imageUrl ? (
          <UnistylesImage
            transition={IMAGE_TRANSITION_DURATION}
            style={style.image}
            source={imageUrl}
            contentFit="cover"
          />
        ) : null}

        <Box background="surface" p="md">
          <Text numberOfLines={2}>{title || ''}</Text>
          <Text
            numberOfLines={3}
            size="xs"
            lineHeight="xs"
            variant="typographySecondary"
          >
            {description || ''}
          </Text>
        </Box>
      </Pressable>
    </Link>
  );
}
const style = StyleSheet.create((theme) => ({
  image: {
    minHeight: 150,
    maxHeight: 250,
    height: undefined,
    aspectRatio: 16 / 9,
    flex: 1,
  },
  linkContainer: {
    textDecorationLine: 'none',
    margin: 0,
    padding: 0,
    display: 'flex',
    width: '100%',
    flex: 1,
  },
  pressed: {
    opacity: 0.7,
  },
  pressable: {
    backgroundColor: theme.colors.surface,
    borderRadius: theme.borderRadius.xl,
    flex: 1,
    marginVertical: theme.size(1),
    maxWidth: 300,
    overflow: 'hidden',
    width: '100%',
  },
}));
